import { ServerApi } from './server-api';
import axios from 'axios';
import { InviteDTO } from './server-types';
import { PolicyDTO } from '../types/roles';
import { getCurrentInstance, appDefIds, isAppInstalled } from '@wix/business-manager-api';
import { addInteractionMonitoring } from './fedops';

export default (msid: string): ServerApi => {
  const INVITES_ENDPOINT = `/_api/invites-web/invites/site`;

  const axiosInstance = axios.create();

  const instance = getCurrentInstance(appDefIds.metaSite);
  const authorizedAxiosInstance = axios.create({
    headers: {
      Authorization: instance
    }
  });

  const fetchTeamMembers = addInteractionMonitoring(() => {
    return authorizedAxiosInstance
      .get(`/_api/roles-management-web/contributors`)
      .then(res => res.data);
  }, 'site-fetch-team-members');

  const inviteContributorsWithPolicyIds = addInteractionMonitoring((emails: string[], policyIds: string[]) => {
    return authorizedAxiosInstance
      .post(`${INVITES_ENDPOINT}/bulk`, { policyIds, emails })
      .then(res => res.data);
  }, 'site-invite-contributors');

  const removeContributor = addInteractionMonitoring((userId: string) => {
    return authorizedAxiosInstance
      .delete(`/_api/authorization-server/v1/account/${userId}/assigned-policies`)
      .then(res => res.data);
  }, 'site-remove-contributor');

  const revokeInvite = addInteractionMonitoring((inviteId: string) => {
    return authorizedAxiosInstance
      .post(`${INVITES_ENDPOINT}/${inviteId}/revoke`)
      .then(res => res.data);
  }, 'site-revoke-invite');

  const resendInvite = addInteractionMonitoring((inviteId: string): Promise<InviteDTO> => {
    return authorizedAxiosInstance
      .post(`${INVITES_ENDPOINT}/${inviteId}/resend`)
      .then(res => res.data.invite);
  }, 'site-resend-invite');

  const setTeamDetails = addInteractionMonitoring((accountId: string, accountName: string, accountImg: string) => {
    return authorizedAxiosInstance
      .patch(`/_api/account-server/v1/accounts/${accountId}`, {
        accountId,
        accountName,
        accountImg: !!accountImg ? accountImg : '',
      })
      .then(res => res.data);
  }, 'site-set-team-details');

  const setInviteDetails = addInteractionMonitoring((inviteId: string, policyIds: string[]) => {
    const setInviteDetailsParams = { policyIds };
    return authorizedAxiosInstance
      .patch(`${INVITES_ENDPOINT}/${inviteId}`, setInviteDetailsParams)
      .then(res => res.data);
  }, 'site-set-invite-details');

  const setContributorDetails = addInteractionMonitoring((userId: string, policyIds: string[]) => {
    const setInviteDetailsParams = { newAssignments: policyIds.map(id => ({ policyId: id })) };
    return authorizedAxiosInstance
      .put(`/_api/authorization-server/v1/account/${userId}/assigned-policies`, {
        ...setInviteDetailsParams,
        userId,
      })
      .then(res => res.data);
  }, 'site-set-contributor-details');

  // TODO: refactor this method. Maybe AccMgr/BizMgr have the data in the framework already
  const fetchCurrentUserDetails = addInteractionMonitoring(async () => {
    const [{ profileImage }, { first, last }] = (await Promise.all([
      authorizedAxiosInstance.get(`/_api/business-manager/user`),
      authorizedAxiosInstance.get(`/_api/my-account-services/user`),
    ])).map(res => res.data);

    return { first, last, profileImage };
  }, 'site-fetch-current-user-details');

  const createRole = addInteractionMonitoring(async (policy: PolicyDTO) => {
    return axios // Roles are never created site-level, therefore we mustn't implicitly specify an msid by using an auth header
        .post('/_api/authorization-server/v1/policies', { policy })
        .then(res => res.data);
  }, 'site-set-role');

  const updateRoleStatements = addInteractionMonitoring(async ({ id, statements }: Pick<PolicyDTO, 'id' | 'statements'>) => {
    return axios // Roles are never created site-level, therefore we mustn't implicitly specify an msid by using an auth header
      .put(`/_api/authorization-server/v1/policies/${id}/authorization-data`, { policyStatements: statements })
      .then(res => res.data);
  }, 'site-update-role-statements');

  const updateRoleMeta = addInteractionMonitoring(async (policy: Omit<PolicyDTO, 'statements'>) => {
    return axios // Roles are never created site-level, therefore we mustn't implicitly specify an msid by using an auth header
      .patch(`/_api/authorization-server/v1/policies/${policy.id}/metadata`, policy)
      .then(res => res.data);
  }, 'site-update-role-meta');

  const removeRole = addInteractionMonitoring(async (policyId: string) => {
    return axios // Roles are never created site-level, therefore we mustn't implicitly specify an msid by using an auth header
      .delete(`/_api/authorization-server/v1/policies/${policyId}`)
      .then(res => res.data);
  }, 'site-remove-role');

  const resendTransferOwnershipEmail = addInteractionMonitoring(() => {
    return axiosInstance
      .get(`transfer-site-webapp/transferSiteOwnership/resendTransferEmail?siteGuid=${msid}`)
      .then(res => res.data);
  }, 'site-resend-transfer-ownership-email');

  const cancelTransferOwnership = addInteractionMonitoring(() => {
    return axiosInstance
      .get(`transfer-site-webapp/transferSiteOwnership/cancelTransfer?siteGuid=${msid}`)
      .then(res => res.data);
  }, 'site-cancel-transfer-ownership');

  const fetchContributorLimit = addInteractionMonitoring(() => {
    return authorizedAxiosInstance
      .get(`/_api/invites-web/invites/limit/site`)
      .then(res => res.data);
  }, 'site-get-contributor-limit');

  const fetchTeamMemberUsers = () => {
    throw 'not implemented';
  };

  return {
    fetchTeamMembers,
    inviteContributorsWithPolicyIds,
    setTeamDetails,
    removeContributor,
    revokeInvite,
    resendInvite,
    setInviteDetails,
    setContributorDetails,
    fetchCurrentUserDetails,
    createRole,
    updateRoleStatements,
    updateRoleMeta,
    removeRole,
    isAppInstalled,
    resendTransferOwnershipEmail,
    cancelTransferOwnership,
    fetchTeamMemberUsers,
    fetchContributorLimit,
  };
};
